import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, Package, Users, FileText, Settings, LogOut, 
  Plus, DollarSign, ShoppingBag, Mail, Eye, Trash2, Edit, X,
  ChevronRight, TrendingUp, BarChart3, Link as LinkIcon, BarChart,
  BookOpen, Bell, Newspaper, ChefHat, Globe, Tag, Star, Phone, MessageSquare, ExternalLink, Check, Download, Store, CreditCard, Truck, Scale,
  ChevronDown, Shield, UserPlus, Lock, Bug, Clock, FileEdit
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { EmailMarketing } from '../components/admin/EmailMarketing';
import { SegmentManager } from '../components/admin/SegmentManager';
import { ContentManager } from '../components/admin/ContentManager';
import RichTextEditor from '../components/RichTextEditor';
import LandingPageManager from '../components/admin/LandingPageManager';
import ABTestingDashboard from '../components/admin/ABTestingDashboard';
import MarketingDashboard from '../components/admin/MarketingDashboard';
import StoreManager from '../components/admin/StoreManager';
import ReferralAffiliateManager from '../components/admin/ReferralAffiliateManager';
import ShippingSettingsTab from '../components/admin/ShippingSettingsTab';
import axios from 'axios';

// Import admin components
import { 
  OverviewTab, 
  ProductsTab, 
  OrdersTab, 
  LeadsTab,
  CouponsTab 
} from '../components/admin';
import PreOrdersTab from '../components/admin/PreOrdersTab';
import BugReportsManager from '../components/admin/BugReportsManager';
import PayoutSchedulerSettings from '../components/admin/PayoutSchedulerSettings';
import EmailTemplatesEditor from '../components/admin/EmailTemplatesEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [pages, setPages] = useState([]);
  const [embeds, setEmbeds] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [reviews, setReviews] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [popupAnalytics, setPopupAnalytics] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [emailSubscribers, setEmailSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [trackingStats, setTrackingStats] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [dailyAnalytics, setDailyAnalytics] = useState([]);
  const [templateAnalytics, setTemplateAnalytics] = useState([]);
  const [subscriberGrowth, setSubscriberGrowth] = useState([]);
  const [unsubscribeList, setUnsubscribeList] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [abandonedCartStats, setAbandonedCartStats] = useState(null);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviewRequestStats, setReviewRequestStats] = useState(null);
  const [abTests, setAbTests] = useState([]);
  const [templateSettings, setTemplateSettings] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deliverabilityStats, setDeliverabilityStats] = useState(null);
  const [deliverabilityDaily, setDeliverabilityDaily] = useState([]);
  const [deliverabilityAlerts, setDeliverabilityAlerts] = useState([]);
  const [suppressionList, setSuppressionList] = useState([]);
  const [campaignBanners, setCampaignBanners] = useState([]);
  const [showBannerDialog, setShowBannerDialog] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    name: '', title: '', text: '', cta_text: 'Shop Now', cta_link: '/products',
    location: 'all', is_active: true, background_color: '#003456'
  });
  
  // Security & Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddTeamMemberDialog, setShowAddTeamMemberDialog] = useState(false);
  const [teamMemberForm, setTeamMemberForm] = useState({
    name: '', email: '', role: 'editor', permissions: []
  });
  
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  
  // Navigation group expansion state
  const [expandedGroups, setExpandedGroups] = useState({
    commerce: true,
    marketing: false,
    admin: false
  });
  
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };
  
  // Competitors state
  const [competitors, setCompetitors] = useState([]);
  const [showCompetitorDialog, setShowCompetitorDialog] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [competitorForm, setCompetitorForm] = useState({
    name: '',
    competitor_type: 'home',
    price: '',
    price_cad: '',
    image_url: '',
    description: '',
    specifications: {},
    features: []
  });
  const [newCompSpecKey, setNewCompSpecKey] = useState('');
  const [newCompSpecValue, setNewCompSpecValue] = useState('');
  const [newCompFeature, setNewCompFeature] = useState('');
  
  // Product form
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', image_url: '', category: 'freeze-dryer',
    features: '', specifications: '', ships_with: ''
  });
  const [showProductDialog, setShowProductDialog] = useState(false);
  
  // Embed form
  const [embedForm, setEmbedForm] = useState({ name: '', embed_url: '', description: '' });
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);

  // Blog form
  const [blogForm, setBlogForm] = useState({ 
    title: '', slug: '', content: '', excerpt: '', featured_image: '', 
    category_id: '', tags: '', published: false, featured: false 
  });
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);

  // Category form
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '' });
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);

  // Recipe form
  const [recipeForm, setRecipeForm] = useState({ 
    title: '', slug: '', description: '', featured_image: '', 
    prep_time: '', rehydration_time: '', cook_time: '', servings: '',
    difficulty: '', categories: '', tags: '', published: false, featured: false,
    ingredients: '', instructions: ''
  });
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState(null);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    google_analytics_id: '',
    google_search_console_verification: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    social_twitter: '',
    phone_number: '',
    commercial_email: 'info@peakprairie.ca',
    support_email: '',
    sms_notifications_enabled: false,
    sms_notification_number: '',
    promo_banner_enabled: false,
    promo_banner_text: '',
    promo_banner_link: ''
  });

  // Coupon form
  const [couponForm, setCouponForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_order: '',
    max_uses: '',
    max_uses_per_user: '1',
    valid_until: '',
    active: true
  });
  const [showCouponDialog, setShowCouponDialog] = useState(false);

  // Document form
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: '',
    category: 'user-manual',
    file_url: '',
    external_url: '',
    file_type: 'pdf',
    is_featured: false
  });
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshOrders = async () => {
    try {
      const ordersRes = await axios.get(`${API_URL}/api/orders`, { headers });
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Failed to refresh orders:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, leadsRes, ordersRes, pagesRes, embedsRes, blogRes, categoriesRes, newsletterRes, alertsRes, recipesRes, settingsRes, reviewsRes, couponsRes, documentsRes, referralsRes, popupRes, emailStatsRes, campaignsRes, subscribersRes, subStatsRes, templatesRes, trackingRes, cronRes, dailyRes, templateStatsRes, growthRes, unsubsRes, schedulerRes, cartStatsRes, cartsRes, reviewReqRes, reviewReqStatsRes, abTestsRes, templateSettingsRes, delivStatsRes, delivDailyRes, delivAlertsRes, suppressRes, bannersRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/leads`, { headers }),
        axios.get(`${API_URL}/api/orders`, { headers }),
        axios.get(`${API_URL}/api/pages`, { headers }),
        axios.get(`${API_URL}/api/financing-embeds`),
        axios.get(`${API_URL}/api/blog/posts?published_only=false`, { headers }),
        axios.get(`${API_URL}/api/blog/categories`),
        axios.get(`${API_URL}/api/newsletter/stats`, { headers }),
        axios.get(`${API_URL}/api/stock-alerts`, { headers }),
        axios.get(`${API_URL}/api/recipes?published_only=false`, { headers }),
        axios.get(`${API_URL}/api/admin/settings`, { headers }),
        axios.get(`${API_URL}/api/admin/reviews`, { headers }),
        axios.get(`${API_URL}/api/coupons`, { headers }),
        axios.get(`${API_URL}/api/documents`),
        axios.get(`${API_URL}/api/admin/referrals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/popup/analytics?days=30`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/admin/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/campaigns`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/subscribers?list_type=all&limit=50`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/subscribers/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/templates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/tracking/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/cron/status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/analytics/daily?days=14`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/analytics/by-template`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/analytics/subscriber-growth?days=14`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/admin/unsubscribes?limit=20`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/scheduler/status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/admin/abandoned-carts/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/admin/abandoned-carts?limit=20`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/admin/review-requests?limit=20`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/admin/review-requests/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/ab-tests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/templates/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/emails/deliverability/stats?days=30`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/emails/deliverability/daily?days=14`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/emails/deliverability/health-alerts`, { headers }).catch(() => ({ data: { alerts: [], health_score: 100 } })),
        axios.get(`${API_URL}/api/emails/deliverability/suppression-list?limit=50`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/marketing/banners`, { headers }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setLeads(leadsRes.data);
      // Count new leads (leads with status 'new')
      const newLeads = leadsRes.data.filter(l => l.status === 'new').length;
      setNewLeadsCount(newLeads);
      setOrders(ordersRes.data);
      setPages(pagesRes.data);
      setEmbeds(embedsRes.data);
      setBlogPosts(blogRes.data.posts || []);
      setBlogCategories(categoriesRes.data);
      setNewsletterStats(newsletterRes.data);
      setStockAlerts(alertsRes.data);
      setRecipes(recipesRes.data.recipes || []);
      setSiteSettings(settingsRes.data || {});
      setSettingsForm(settingsRes.data || {});
      setReviews(reviewsRes.data || []);
      setCoupons(couponsRes.data || []);
      setDocuments(documentsRes.data || []);
      setReferrals(referralsRes.data || []);
      setPopupAnalytics(popupRes.data || null);
      setEmailStats(emailStatsRes.data || null);
      setEmailCampaigns(campaignsRes.data || []);
      setEmailSubscribers(subscribersRes.data || []);
      setSubscriberStats(subStatsRes.data || null);
      setEmailTemplates(templatesRes.data || []);
      setTrackingStats(trackingRes.data || null);
      setCronStatus(cronRes.data || null);
      setDailyAnalytics(dailyRes.data || []);
      setTemplateAnalytics(templateStatsRes.data || []);
      setSubscriberGrowth(growthRes.data || []);
      setUnsubscribeList(unsubsRes.data || []);
      setSchedulerStatus(schedulerRes.data || null);
      setAbandonedCartStats(cartStatsRes.data || null);
      setAbandonedCarts(cartsRes.data || []);
      setReviewRequests(reviewReqRes.data || []);
      setReviewRequestStats(reviewReqStatsRes.data || null);
      setAbTests(abTestsRes.data || []);
      setTemplateSettings(templateSettingsRes.data || {});
      setDeliverabilityStats(delivStatsRes.data || null);
      setDeliverabilityDaily(delivDailyRes.data || []);
      setDeliverabilityAlerts(delivAlertsRes.data?.alerts || []);
      setSuppressionList(suppressRes.data || []);
      setCampaignBanners(bannersRes.data || []);
      
      // Fetch competitors
      try {
        const competitorsRes = await axios.get(`${API_URL}/api/competitors`, { headers });
        setCompetitors(competitorsRes.data || []);
      } catch (e) {
        console.log('Competitors API not available');
      }
      
      // Fetch team members
      try {
        const teamRes = await axios.get(`${API_URL}/api/team/members`, { headers });
        setTeamMembers(teamRes.data.members || []);
      } catch (e) {
        console.log('Team API not available');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        features: productForm.features ? productForm.features.split(',').map(f => f.trim()) : [],
        specifications: productForm.specifications ? JSON.parse(productForm.specifications) : {},
        ships_with: productForm.ships_with ? productForm.ships_with.split(',').map(s => s.trim()).filter(s => s) : []
      };
      await axios.post(`${API_URL}/api/products`, data, { headers });
      toast.success('Product created!');
      setShowProductDialog(false);
      setProductForm({ name: '', description: '', price: '', image_url: '', category: 'freeze-dryer', features: '', specifications: '', ships_with: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`, { headers });
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Competitor management functions
  const handleSaveCompetitor = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: competitorForm.name,
        competitor_type: competitorForm.competitor_type,
        price: parseFloat(competitorForm.price),
        price_cad: competitorForm.price_cad ? parseFloat(competitorForm.price_cad) : null,
        image_url: competitorForm.image_url || null,
        description: competitorForm.description || null,
        specifications: competitorForm.specifications || {},
        features: competitorForm.features || [],
        is_active: true
      };
      
      if (editingCompetitor) {
        await axios.put(`${API_URL}/api/competitors/${editingCompetitor.id}`, data, { headers });
        toast.success('Competitor updated!');
      } else {
        await axios.post(`${API_URL}/api/competitors`, data, { headers });
        toast.success('Competitor created!');
      }
      
      setShowCompetitorDialog(false);
      setEditingCompetitor(null);
      setCompetitorForm({ name: '', competitor_type: 'home', price: '', price_cad: '', image_url: '', description: '', specifications: {}, features: [] });
      setNewCompSpecKey('');
      setNewCompSpecValue('');
      setNewCompFeature('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save competitor');
    }
  };

  const handleEditCompetitor = (competitor) => {
    setEditingCompetitor(competitor);
    setCompetitorForm({
      name: competitor.name,
      competitor_type: competitor.competitor_type,
      price: competitor.price?.toString() || '',
      price_cad: competitor.price_cad?.toString() || '',
      image_url: competitor.image_url || '',
      description: competitor.description || '',
      specifications: competitor.specifications || {},
      features: competitor.features || []
    });
    setShowCompetitorDialog(true);
  };

  // Competitor Specification helpers
  const addCompSpec = () => {
    if (!newCompSpecKey.trim() || !newCompSpecValue.trim()) return;
    setCompetitorForm({
      ...competitorForm,
      specifications: { ...competitorForm.specifications, [newCompSpecKey]: newCompSpecValue }
    });
    setNewCompSpecKey('');
    setNewCompSpecValue('');
  };

  const removeCompSpec = (key) => {
    const newSpecs = { ...competitorForm.specifications };
    delete newSpecs[key];
    setCompetitorForm({ ...competitorForm, specifications: newSpecs });
  };

  // Competitor Feature helpers
  const addCompFeature = () => {
    if (!newCompFeature.trim()) return;
    setCompetitorForm({
      ...competitorForm,
      features: [...(competitorForm.features || []), newCompFeature.trim()]
    });
    setNewCompFeature('');
  };

  const removeCompFeature = (index) => {
    const newFeatures = [...competitorForm.features];
    newFeatures.splice(index, 1);
    setCompetitorForm({ ...competitorForm, features: newFeatures });
  };

  const handleDeleteCompetitor = async (id) => {
    if (!window.confirm('Delete this competitor?')) return;
    try {
      await axios.delete(`${API_URL}/api/competitors/${id}`, { headers });
      toast.success('Competitor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete competitor');
    }
  };

  const handleCreateEmbed = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/financing-embeds`, embedForm, { headers });
      toast.success('Financing embed created!');
      setShowEmbedDialog(false);
      setEmbedForm({ name: '', embed_url: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create embed');
    }
  };

  const handleDeleteEmbed = async (id) => {
    if (!window.confirm('Delete this financing embed?')) return;
    try {
      await axios.delete(`${API_URL}/api/financing-embeds/${id}`, { headers });
      toast.success('Embed deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete embed');
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await axios.delete(`${API_URL}/api/pages/${id}`, { headers });
      toast.success('Page deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  // Blog handlers
  const handleCreateBlogPost = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...blogForm,
        tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()) : []
      };
      
      if (editingBlogId) {
        await axios.put(`${API_URL}/api/blog/posts/${editingBlogId}`, data, { headers });
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API_URL}/api/blog/posts`, data, { headers });
        toast.success('Blog post created!');
      }
      
      setShowBlogDialog(false);
      setBlogForm({ title: '', slug: '', content: '', excerpt: '', featured_image: '', category_id: '', tags: '', published: false, featured: false });
      setEditingBlogId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const handleEditBlogPost = (post) => {
    setBlogForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      category_id: post.category_id || '',
      tags: post.tags?.join(', ') || '',
      published: post.published,
      featured: post.featured
    });
    setEditingBlogId(post.id);
    setShowBlogDialog(true);
  };

  const handleDeleteBlogPost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/api/blog/posts/${id}`, { headers });
      toast.success('Blog post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/blog/categories`, categoryForm, { headers });
      toast.success('Category created!');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', slug: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/api/blog/categories/${id}`, { headers });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Recipe handlers
  const handleCreateRecipe = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...recipeForm,
        prep_time: recipeForm.prep_time ? parseInt(recipeForm.prep_time) : null,
        rehydration_time: recipeForm.rehydration_time ? parseInt(recipeForm.rehydration_time) : null,
        cook_time: recipeForm.cook_time ? parseInt(recipeForm.cook_time) : null,
        servings: recipeForm.servings ? parseInt(recipeForm.servings) : null,
        categories: recipeForm.categories ? recipeForm.categories.split(',').map(c => c.trim()) : [],
        tags: recipeForm.tags ? recipeForm.tags.split(',').map(t => t.trim()) : [],
        ingredients_html: recipeForm.ingredients,
        instructions_html: recipeForm.instructions
      };
      
      if (editingRecipeId) {
        await axios.put(`${API_URL}/api/recipes/${editingRecipeId}`, data, { headers });
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API_URL}/api/recipes`, data, { headers });
        toast.success('Recipe created!');
      }
      
      setShowRecipeDialog(false);
      setRecipeForm({ title: '', slug: '', description: '', featured_image: '', prep_time: '', rehydration_time: '', cook_time: '', servings: '', difficulty: '', categories: '', tags: '', published: false, featured: false });
      setEditingRecipeId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    }
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Delete this recipe?')) return;
    try {
      await axios.delete(`${API_URL}/api/recipes/${id}`, { headers });
      toast.success('Recipe deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  // Settings handlers
  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settingsForm, { headers });
      toast.success('Settings updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Coupon handlers
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...couponForm,
        discount_value: parseFloat(couponForm.discount_value),
        minimum_order: couponForm.minimum_order ? parseFloat(couponForm.minimum_order) : null,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        max_uses_per_user: parseInt(couponForm.max_uses_per_user) || 1
      };
      
      await axios.post(`${API_URL}/api/coupons`, data, { headers });
      toast.success('Coupon created!');
      setShowCouponDialog(false);
      setCouponForm({ code: '', description: '', discount_type: 'percentage', discount_value: '', minimum_order: '', max_uses: '', max_uses_per_user: '1', valid_until: '', active: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API_URL}/api/coupons/${id}`, { headers });
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API_URL}/api/reviews/${id}`, { headers });
      toast.success('Review deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  // Navigation structure with groups
  const navGroups = [
    {
      id: 'commerce',
      label: 'Products & Commerce',
      icon: ShoppingBag,
      items: [
        { id: 'preorders', icon: CreditCard, label: 'Pre-Orders' },
        { id: 'catalogue', icon: Package, label: 'Catalogue' },
        { id: 'store', icon: Store, label: 'Store & Inventory' },
        { id: 'orders', icon: ShoppingBag, label: 'Orders & Carts' },
        { id: 'shipping', icon: Truck, label: 'Shipping Rates' },
        { id: 'referrals', icon: DollarSign, label: 'Referrals & Affiliates' },
      ]
    },
    {
      id: 'marketing',
      label: 'Marketing',
      icon: Tag,
      items: [
        { id: 'leads', icon: Users, label: 'Leads & Forms', badge: newLeadsCount },
        { id: 'competitors', icon: Scale, label: 'Competitors' },
        { id: 'marketing', icon: Tag, label: 'Marketing Hub' },
        { id: 'landing-pages', icon: LinkIcon, label: 'Landing Pages' },
        { id: 'ab-testing', icon: BarChart, label: 'A/B Testing' },
        { id: 'email', icon: Mail, label: 'Email Marketing' },
        { id: 'content', icon: BookOpen, label: 'Content' },
        { id: 'testimonials', icon: Star, label: 'Testimonials' },
      ]
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Settings,
      items: [
        { id: 'documents', icon: FileText, label: 'Documents' },
        { id: 'bug-reports', icon: Bug, label: 'Bug Reports' },
        { id: 'pages', icon: Globe, label: 'Website Control' },
        { id: 'payout-scheduler', icon: Clock, label: 'Payout Scheduler' },
        { id: 'email-templates', icon: FileEdit, label: 'Email Templates' },
        { id: 'settings', icon: Settings, label: 'SEO & Settings' },
        { id: 'security', icon: Shield, label: 'Security & Team' },
      ]
    }
  ];

  // Keep flat navItems for backward compatibility
  const navItems = [
    { id: 'overview', icon: Home, label: 'Overview' },
    { id: 'leads', icon: Users, label: 'Leads & Forms', badge: newLeadsCount },
    { id: 'preorders', icon: CreditCard, label: 'Pre-Orders' },
    { id: 'catalogue', icon: Package, label: 'Catalogue' },
    { id: 'store', icon: Store, label: 'Store & Inventory' },
    { id: 'orders', icon: ShoppingBag, label: 'Orders & Carts' },
    { id: 'shipping', icon: Truck, label: 'Shipping Rates' },
    { id: 'competitors', icon: Scale, label: 'Competitors' },
    { id: 'marketing', icon: Tag, label: 'Marketing' },
    { id: 'landing-pages', icon: LinkIcon, label: 'Landing Pages' },
    { id: 'ab-testing', icon: BarChart, label: 'A/B Testing' },
    { id: 'email', icon: Mail, label: 'Email Marketing' },
    { id: 'content', icon: BookOpen, label: 'Content' },
    { id: 'testimonials', icon: Star, label: 'Testimonials' },
    { id: 'documents', icon: FileText, label: 'Documents' },
    { id: 'referrals', icon: DollarSign, label: 'Referrals' },
    { id: 'bug-reports', icon: Bug, label: 'Bug Reports' },
    { id: 'email-templates', icon: FileEdit, label: 'Email Templates' },
    { id: 'payout-scheduler', icon: Clock, label: 'Payouts' },
    { id: 'pages', icon: Globe, label: 'Website Control' },
    { id: 'settings', icon: Settings, label: 'SEO & Settings' },
    { id: 'security', icon: Shield, label: 'Security & Team' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f4f8] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0a0203] min-h-screen p-6 fixed left-0 top-0">
        <div className="flex items-center gap-2 mb-10">
          <Logo size="md" />
          <span className="text-xl font-bold text-white" style={{  }}>HomeLyph</span>
        </div>
        
        <nav className="space-y-1 overflow-y-auto max-h-[calc(100vh-280px)]">
          {/* Overview - Always visible at top */}
          <button
            onClick={() => setActiveSection('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeSection === 'overview'
                ? 'bg-[#003456] text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid="nav-overview"
          >
            <Home className="h-5 w-5" />
            <span className="font-medium flex-1 text-left">Overview</span>
          </button>

          {/* Grouped Navigation */}
          {navGroups.map(group => (
            <div key={group.id} className="pt-2">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white transition-colors"
                data-testid={`nav-group-${group.id}`}
              >
                <group.icon className="h-4 w-4" />
                <span className="font-semibold flex-1 text-left text-sm uppercase tracking-wider">{group.label}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${expandedGroups[group.id] ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Group Items */}
              {expandedGroups[group.id] && (
                <div className="ml-2 space-y-1 mt-1">
                  {group.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${
                        activeSection === item.id
                          ? 'bg-[#003456] text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`nav-${item.id}`}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="font-medium flex-1 text-left">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 bg-white/5 rounded-xl mb-4">
            <p className="text-sm text-gray-400">Logged in as</p>
            <p className="text-white font-medium truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Overview */}
        {activeSection === 'overview' && (
          <OverviewTab
            stats={stats}
            productForm={productForm}
            setProductForm={setProductForm}
            showProductDialog={showProductDialog}
            setShowProductDialog={setShowProductDialog}
            handleCreateProduct={handleCreateProduct}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Products - Catalogue */}
        {activeSection === 'catalogue' && (
          <ProductsTab
            products={products}
            productForm={productForm}
            setProductForm={setProductForm}
            showProductDialog={showProductDialog}
            setShowProductDialog={setShowProductDialog}
            handleCreateProduct={handleCreateProduct}
            handleDeleteProduct={handleDeleteProduct}
            headers={headers}
            fetchData={fetchData}
          />
        )}

        {/* Orders */}
        {activeSection === 'orders' && (
          <OrdersTab orders={orders} headers={headers} onOrderUpdate={refreshOrders} />
        )}

        {/* Shipping Settings */}
        {activeSection === 'shipping' && (
          <div className="animate-fade-in">
            <ShippingSettingsTab />
          </div>
        )}

        {/* Competitors Management */}
        {activeSection === 'competitors' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-[#0a0203]">Competitor Management</h2>
                <p className="text-gray-500">Add competitor products for comparison (without naming them directly)</p>
              </div>
              <Dialog open={showCompetitorDialog} onOpenChange={setShowCompetitorDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-[#003456]"
                    onClick={() => {
                      setEditingCompetitor(null);
                      setCompetitorForm({ name: '', competitor_type: 'home', price: '', image_url: '', description: '', specifications: {}, features: [] });
                      setNewCompSpecKey('');
                      setNewCompSpecValue('');
                      setNewCompFeature('');
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Competitor
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingCompetitor ? 'Edit Competitor' : 'Add Competitor'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveCompetitor} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Display Name</label>
                      <Input
                        value={competitorForm.name}
                        onChange={(e) => setCompetitorForm({ ...competitorForm, name: e.target.value })}
                        placeholder="e.g., Leading Competitor"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Use a generic name - avoid actual brand names</p>
                    </div>
                    
                    {/* Pricing Section */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <label className="text-sm font-medium flex items-center gap-2 mb-3">
                        <DollarSign className="h-4 w-4 text-green-600" /> Pricing
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">USD Price ($)</label>
                          <Input
                            type="number"
                            value={competitorForm.price}
                            onChange={(e) => setCompetitorForm({ ...competitorForm, price: e.target.value })}
                            placeholder="2995"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">CAD Price ($)</label>
                          <Input
                            type="number"
                            value={competitorForm.price_cad}
                            onChange={(e) => setCompetitorForm({ ...competitorForm, price_cad: e.target.value })}
                            placeholder="3995"
                          />
                          <p className="text-xs text-gray-400 mt-1">Leave empty to auto-calculate</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Competitor Type</label>
                        <select
                          value={competitorForm.competitor_type}
                          onChange={(e) => setCompetitorForm({ ...competitorForm, competitor_type: e.target.value })}
                          className="w-full border rounded-lg p-2"
                        >
                          <option value="home">Home Use</option>
                          <option value="small-business">Small Business</option>
                          <option value="commercial">Commercial</option>
                          <option value="industrial">Industrial</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Image URL (optional)</label>
                        <Input
                          value={competitorForm.image_url}
                          onChange={(e) => setCompetitorForm({ ...competitorForm, image_url: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Description (optional)</label>
                      <Input
                        value={competitorForm.description}
                        onChange={(e) => setCompetitorForm({ ...competitorForm, description: e.target.value })}
                        placeholder="Brief description"
                      />
                    </div>
                    
                    {/* Specifications Table */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <label className="text-sm font-medium flex items-center gap-2 mb-3">
                        <Settings className="h-4 w-4" /> Specifications
                      </label>
                      {Object.keys(competitorForm.specifications || {}).length > 0 && (
                        <div className="mb-3 rounded-lg border overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">Specification</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">Value</th>
                                <th className="px-3 py-2 w-10"></th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                              {Object.entries(competitorForm.specifications).map(([key, value]) => (
                                <tr key={key} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 font-medium text-gray-700">{key}</td>
                                  <td className="px-3 py-2 text-gray-600">{value}</td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() => removeCompSpec(key)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Spec name (e.g., Tray Space)"
                          value={newCompSpecKey}
                          onChange={(e) => setNewCompSpecKey(e.target.value)}
                          className="w-1/3"
                        />
                        <Input
                          placeholder="Value (e.g., 6 sq ft)"
                          value={newCompSpecValue}
                          onChange={(e) => setNewCompSpecValue(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="button" onClick={addCompSpec} variant="outline" size="sm">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Features List */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <label className="text-sm font-medium flex items-center gap-2 mb-3">
                        <Star className="h-4 w-4 text-blue-600" /> Features
                      </label>
                      {(competitorForm.features || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {competitorForm.features.map((feature, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full border text-sm">
                              {feature}
                              <button
                                type="button"
                                onClick={() => removeCompFeature(idx)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a feature..."
                          value={newCompFeature}
                          onChange={(e) => setNewCompFeature(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCompFeature())}
                          className="flex-1"
                        />
                        <Button type="button" onClick={addCompFeature} variant="outline" size="sm">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full bg-[#003456]">
                      {editingCompetitor ? 'Update Competitor' : 'Create Competitor'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Competitors List */}
            {competitors.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl">
                <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No competitors added yet</p>
                <p className="text-sm text-gray-400">Add competitor products to show on the Compare page</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {competitors.map((competitor) => (
                  <div key={competitor.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {competitor.image_url && (
                      <img 
                        src={competitor.image_url} 
                        alt={competitor.name}
                        className="w-full h-32 object-contain bg-gray-50"
                      />
                    )}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-[#0a0203]">{competitor.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          competitor.competitor_type === 'home' ? 'bg-blue-100 text-blue-700' :
                          competitor.competitor_type === 'commercial' ? 'bg-purple-100 text-purple-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {competitor.competitor_type}
                        </span>
                      </div>
                      <div className="mb-3">
                        <div className="flex items-baseline gap-2">
                          <p className="text-2xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                            ${competitor.price?.toLocaleString()}
                          </p>
                          <span className="text-xs text-gray-500">USD</span>
                        </div>
                        {competitor.price_cad && (
                          <div className="flex items-baseline gap-2 mt-1">
                            <p className="text-lg font-semibold text-gray-600" style={{ fontFamily: 'JetBrains Mono' }}>
                              ${competitor.price_cad?.toLocaleString()}
                            </p>
                            <span className="text-xs text-gray-500">CAD</span>
                          </div>
                        )}
                      </div>
                      {competitor.description && (
                        <p className="text-sm text-gray-500 mb-3">{competitor.description}</p>
                      )}
                      
                      {/* Specifications Table */}
                      {competitor.specifications && Object.keys(competitor.specifications).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                            <Settings className="h-3 w-3" /> Specifications
                          </p>
                          <div className="rounded border overflow-hidden">
                            <table className="w-full text-xs">
                              <tbody className="divide-y">
                                {Object.entries(competitor.specifications).slice(0, 4).map(([key, value]) => (
                                  <tr key={key} className="bg-gray-50">
                                    <td className="px-2 py-1.5 font-medium text-gray-700 w-1/2">{key}</td>
                                    <td className="px-2 py-1.5 text-gray-600">{value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {Object.keys(competitor.specifications).length > 4 && (
                              <p className="text-xs text-gray-400 px-2 py-1 bg-gray-50 border-t">
                                +{Object.keys(competitor.specifications).length - 4} more specs
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Features Tags */}
                      {competitor.features && competitor.features.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                            <Star className="h-3 w-3" /> Features
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {competitor.features.slice(0, 4).map((f, i) => (
                              <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{f}</span>
                            ))}
                            {competitor.features.length > 4 && (
                              <span className="text-xs text-gray-400 px-2 py-1">+{competitor.features.length - 4} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditCompetitor(competitor)}
                          className="flex-1"
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCompetitor(competitor.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leads */}
        {activeSection === 'leads' && (
          <LeadsTab leads={leads} headers={headers} />
        )}

        {/* Pre-Orders Management */}
        {activeSection === 'preorders' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-[#0a0203]">Pre-Order Management</h2>
                <p className="text-gray-500">Manage customer pre-orders, deposits, and fulfillment</p>
              </div>
            </div>
            <PreOrdersTab />
          </div>
        )}

        {/* Store & Inventory Management */}
        {activeSection === 'store' && (
          <div className="animate-fade-in">
            <StoreManager headers={headers} />
          </div>
        )}

        {/* Marketing - Analytics Dashboard */}
        {activeSection === 'marketing' && (
          <MarketingDashboard headers={headers} />
        )}

        {/* Email Marketing Section */}
        {/* Email Marketing Section */}
        {activeSection === 'email' && (
          <EmailMarketing
            headers={headers}
            fetchData={fetchData}
            subscriberStats={subscriberStats}
            emailStats={emailStats}
            trackingStats={trackingStats}
            schedulerStatus={schedulerStatus}
            deliverabilityStats={deliverabilityStats}
            deliverabilityDaily={deliverabilityDaily}
            deliverabilityAlerts={deliverabilityAlerts}
            suppressionList={suppressionList}
            emailCampaigns={emailCampaigns}
            emailSubscribers={emailSubscribers}
            emailTemplates={emailTemplates}
            dailyAnalytics={dailyAnalytics}
            templateAnalytics={templateAnalytics}
            subscriberGrowth={subscriberGrowth}
            unsubscribeList={unsubscribeList}
            abandonedCarts={abandonedCarts}
            abandonedCartStats={abandonedCartStats}
            reviewRequests={reviewRequests}
            reviewRequestStats={reviewRequestStats}
            abTests={abTests}
            templateSettings={templateSettings}
          />
        )}

        {/* Landing Pages Manager */}
        {activeSection === 'landing-pages' && (
          <div className="animate-fade-in">
            <LandingPageManager token={token} />
          </div>
        )}

        {/* A/B Testing Dashboard */}
        {activeSection === 'ab-testing' && (
          <div className="animate-fade-in">
            <ABTestingDashboard token={token} />
          </div>
        )}

        {/* Content Management - Blogs & Recipes */}
        {activeSection === 'content' && (
          <ContentManager headers={headers} token={token} />
        )}

        {/* Testimonials Management */}
        {activeSection === 'testimonials' && (
          <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-[#0a0203] mb-8">Customer Testimonials</h1>

            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Total Reviews</p>
                <p className="text-3xl font-bold text-[#003456]">{reviews.length}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-3xl font-bold text-green-600">{reviews.filter(r => r.approved).length}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{reviews.filter(r => !r.approved).length}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Avg Rating</p>
                <p className="text-3xl font-bold text-[#003456]">
                  {reviews.length > 0 ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1) : '0'}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm">
              {reviews.length === 0 ? (
                <div className="p-12 text-center">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Reviews Yet</h3>
                  <p className="text-gray-500">Customer reviews will appear here when submitted.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {reviews.map(review => (
                    <div key={review.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-[#0a0203]">{review.reviewer_name}</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                              ))}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${review.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {review.approved ? 'Approved' : 'Pending'}
                            </span>
                            {review.verified_purchase && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Verified Purchase</span>
                            )}
                          </div>
                          <h4 className="font-medium text-[#0a0203] mb-1">{review.title}</h4>
                          <p className="text-gray-600 text-sm mb-2">{review.content}</p>
                          <p className="text-xs text-gray-400">
                            {review.product_name && `Product: ${review.product_name} • `}
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {!review.approved && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={async () => {
                                await axios.put(`${API_URL}/api/reviews/${review.id}/approve`, {}, { headers });
                                fetchData();
                                toast.success('Review approved');
                              }}
                            >
                              Approve
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (confirm('Delete this review?')) {
                                await axios.delete(`${API_URL}/api/reviews/${review.id}`, { headers });
                                fetchData();
                                toast.success('Review deleted');
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Documents Management */}
        {activeSection === 'documents' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#0a0203]">Documents & Resources</h1>
              <Button 
                onClick={() => {
                  setDocumentForm({ title: '', description: '', category: 'user-manual', file_url: '', external_url: '', file_type: 'pdf', is_featured: false });
                  setEditingDocumentId(null);
                  setShowDocumentDialog(true);
                }} 
                className="bg-[#003456] hover:bg-[#001d30]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm">
              {documents.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Documents Yet</h3>
                  <p className="text-gray-500">Add user manuals, recipe books, and other resources.</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-[#f0f4f8]">
                    <tr>
                      <th className="text-left p-4 font-semibold text-gray-700">Title</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Category</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Type</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Featured</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(doc => (
                      <tr key={doc.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#0a0203]">{doc.title}</p>
                            {doc.description && <p className="text-sm text-gray-500 truncate max-w-xs">{doc.description}</p>}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-[#dbeef5] text-[#003456] rounded text-xs capitalize">
                            {doc.category.replace('-', ' ')}
                          </span>
                        </td>
                        <td className="p-4 uppercase text-xs text-gray-500">{doc.file_type}</td>
                        <td className="p-4">
                          {doc.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setDocumentForm(doc);
                                setEditingDocumentId(doc.id);
                                setShowDocumentDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700"
                              onClick={async () => {
                                if (confirm('Delete this document?')) {
                                  await axios.delete(`${API_URL}/api/admin/documents/${doc.id}`, { headers });
                                  fetchData();
                                  toast.success('Document deleted');
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Document Dialog */}
            <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingDocumentId ? 'Edit Document' : 'Add Document'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingDocumentId) {
                      await axios.put(`${API_URL}/api/admin/documents/${editingDocumentId}`, documentForm, { headers });
                      toast.success('Document updated');
                    } else {
                      await axios.post(`${API_URL}/api/admin/documents`, documentForm, { headers });
                      toast.success('Document added');
                    }
                    setShowDocumentDialog(false);
                    fetchData();
                  } catch (error) {
                    toast.error('Failed to save document');
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <Input value={documentForm.title} onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea 
                      className="w-full p-2 border rounded-md text-sm" 
                      rows={2} 
                      value={documentForm.description || ''} 
                      onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category *</label>
                      <select 
                        className="w-full p-2 border rounded-md" 
                        value={documentForm.category}
                        onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                      >
                        <option value="user-manual">User Manual</option>
                        <option value="recipe-book">Recipe Book</option>
                        <option value="quick-start">Quick Start Guide</option>
                        <option value="maintenance">Maintenance Guide</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">File Type</label>
                      <select 
                        className="w-full p-2 border rounded-md" 
                        value={documentForm.file_type}
                        onChange={(e) => setDocumentForm({ ...documentForm, file_type: e.target.value })}
                      >
                        <option value="pdf">PDF</option>
                        <option value="doc">Word Doc</option>
                        <option value="video">Video</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">File URL (Upload)</label>
                    <Input 
                      placeholder="https://..." 
                      value={documentForm.file_url || ''} 
                      onChange={(e) => setDocumentForm({ ...documentForm, file_url: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">External URL</label>
                    <Input 
                      placeholder="https://drive.google.com/..." 
                      value={documentForm.external_url || ''} 
                      onChange={(e) => setDocumentForm({ ...documentForm, external_url: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="is_featured" 
                      checked={documentForm.is_featured}
                      onChange={(e) => setDocumentForm({ ...documentForm, is_featured: e.target.checked })}
                    />
                    <label htmlFor="is_featured" className="text-sm">Featured Document</label>
                  </div>
                  <Button type="submit" className="w-full bg-[#003456]">
                    {editingDocumentId ? 'Update Document' : 'Add Document'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Referrals & Affiliates Management */}
        {activeSection === 'referrals' && (
          <ReferralAffiliateManager headers={headers} />
        )}

        {/* SEO & Settings */}
        {activeSection === 'settings' && (
          <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-[#0a0203] mb-8" style={{  }}>SEO & Settings</h1>
            
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <form onSubmit={handleUpdateSettings} className="space-y-8">
                {/* Contact Settings */}
                <div>
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-[#003456]" />
                    Contact Settings
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (1-800)</label>
                      <Input 
                        placeholder="1-800-123-4567" 
                        value={settingsForm.phone_number || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, phone_number: e.target.value })}
                        data-testid="phone-number-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Displayed in the top navigation bar</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Commercial Inquiry Email</label>
                      <Input 
                        type="email"
                        placeholder="info@peakprairie.ca" 
                        value={settingsForm.commercial_email || 'info@peakprairie.ca'} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, commercial_email: e.target.value })}
                        data-testid="commercial-email-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Receives commercial/D-Freeze inquiries</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
                      <Input 
                        type="email"
                        placeholder="support@homelyph.com" 
                        value={settingsForm.support_email || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, support_email: e.target.value })}
                        data-testid="support-email-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">General support inquiries</p>
                    </div>
                  </div>
                </div>

                {/* SMS Notifications */}
                <div className="bg-gradient-to-r from-[#003456]/5 to-transparent p-6 rounded-xl border border-[#003456]/10">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-[#003456]" />
                    SMS Notifications (Twilio)
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Receive instant SMS alerts when high-priority commercial inquiries are submitted.
                    Requires Twilio credentials to be configured in the server environment.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="sms-enabled"
                        checked={settingsForm.sms_notifications_enabled || false}
                        onChange={(e) => setSettingsForm({ ...settingsForm, sms_notifications_enabled: e.target.checked })}
                        className="w-5 h-5 text-[#003456] rounded border-gray-300 focus:ring-[#003456]"
                        data-testid="sms-enabled-checkbox"
                      />
                      <label htmlFor="sms-enabled" className="text-sm font-medium text-gray-700">
                        Enable SMS notifications for commercial inquiries
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notification Phone Number</label>
                      <Input 
                        type="tel"
                        placeholder="+1 (555) 000-0000" 
                        value={settingsForm.sms_notification_number || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, sms_notification_number: e.target.value })}
                        disabled={!settingsForm.sms_notifications_enabled}
                        className={!settingsForm.sms_notifications_enabled ? 'opacity-50' : ''}
                        data-testid="sms-number-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Phone number to receive SMS alerts (include country code)</p>
                    </div>
                  </div>
                </div>

                {/* Team Notification Emails */}
                <div className="bg-gradient-to-r from-green-500/5 to-transparent p-6 rounded-xl border border-green-500/10">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-green-600" />
                    Team Notifications
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure email notifications for your team. Add multiple emails separated by commas.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Order Notifications</label>
                      <Input 
                        type="text"
                        placeholder="orders@homelyph.com, sales@homelyph.com" 
                        value={settingsForm.order_notification_emails || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, order_notification_emails: e.target.value })}
                        data-testid="order-notification-emails-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Receive email immediately when a new order is placed.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Low Stock Alerts (Daily Digest)</label>
                      <Input 
                        type="text"
                        placeholder="inventory@homelyph.com, manager@homelyph.com" 
                        value={settingsForm.stock_alert_emails || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, stock_alert_emails: e.target.value })}
                        data-testid="stock-alert-emails-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Receive daily email digest of products with low stock or out of stock.</p>
                    </div>
                  </div>
                </div>

                {/* Promotional Banner */}
                <div className="bg-gradient-to-r from-[#fdc31c]/10 to-transparent p-6 rounded-xl border border-[#fdc31c]/20">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Newspaper className="h-5 w-5 text-[#fdc31c]" />
                    Promotional Banner
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Display a promotional banner at the top of your website to announce sales, events, or special offers.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="promo-enabled"
                        checked={settingsForm.promo_banner_enabled || false}
                        onChange={(e) => setSettingsForm({ ...settingsForm, promo_banner_enabled: e.target.checked })}
                        className="w-5 h-5 text-[#fdc31c] rounded border-gray-300 focus:ring-[#fdc31c]"
                        data-testid="promo-enabled-checkbox"
                      />
                      <label htmlFor="promo-enabled" className="text-sm font-medium text-gray-700">
                        Enable promotional banner
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Banner Text</label>
                        <Input 
                          placeholder="New Year Sale - Up to $500 Off!"
                          value={settingsForm.promo_banner_text || ''} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, promo_banner_text: e.target.value })}
                          disabled={!settingsForm.promo_banner_enabled}
                          className={!settingsForm.promo_banner_enabled ? 'opacity-50' : ''}
                          data-testid="promo-text-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Link (optional)</label>
                        <Input 
                          placeholder="/products"
                          value={settingsForm.promo_banner_link || ''} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, promo_banner_link: e.target.value })}
                          disabled={!settingsForm.promo_banner_enabled}
                          className={!settingsForm.promo_banner_enabled ? 'opacity-50' : ''}
                          data-testid="promo-link-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Where should "Shop Now" link to?</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media Integration */}
                <div className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-blue-500/10 p-6 rounded-xl border border-purple-200">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <LinkIcon className="h-5 w-5 text-purple-600" />
                    Social Media Integration
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Connect your social media accounts to display feeds on your website and in the footer.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Username</label>
                      <Input 
                        placeholder="@yourusername"
                        value={settingsForm.social_instagram || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, social_instagram: e.target.value.replace('@', '') })}
                        data-testid="social-instagram-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Just the username, without @</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">X (Twitter) Username</label>
                      <Input 
                        placeholder="@yourusername"
                        value={settingsForm.social_twitter || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, social_twitter: e.target.value.replace('@', '') })}
                        data-testid="social-twitter-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Just the username, without @</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Facebook Page ID</label>
                      <Input 
                        placeholder="yourpagename"
                        value={settingsForm.social_facebook || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, social_facebook: e.target.value })}
                        data-testid="social-facebook-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Your Facebook page name or ID</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Channel</label>
                      <Input 
                        placeholder="channel/UCxxxxx or @channelname"
                        value={settingsForm.social_youtube || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, social_youtube: e.target.value })}
                        data-testid="social-youtube-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Channel ID or handle</p>
                    </div>
                  </div>
                </div>

                {/* Google Integrations */}
                <div>
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-[#003456]" />
                    Google Integrations
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Google Analytics ID</label>
                      <Input 
                        placeholder="G-XXXXXXXXXX" 
                        value={settingsForm.google_analytics_id || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, google_analytics_id: e.target.value })}
                        data-testid="ga-id-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Find this in your Google Analytics property settings</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Search Console Verification</label>
                      <Input 
                        placeholder="google-site-verification=XXXXXX" 
                        value={settingsForm.google_search_console_verification || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, google_search_console_verification: e.target.value })}
                        data-testid="gsc-verification-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">HTML meta tag verification code</p>
                    </div>
                  </div>
                </div>

                {/* Meta Tags */}
                <div>
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4">Default Meta Tags</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
                      <Input 
                        placeholder="HomeLyph - Premium Home Freeze Dryers" 
                        value={settingsForm.meta_title || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, meta_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                      <textarea 
                        placeholder="Preserve food for up to 25 years with HomeLyph freeze dryers..." 
                        value={settingsForm.meta_description || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, meta_description: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Keywords</label>
                      <Input 
                        placeholder="freeze dryer, food preservation, HomeLyph, home freeze drying" 
                        value={settingsForm.meta_keywords || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, meta_keywords: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Live Chat (Tawk.to) */}
                <div className="bg-gradient-to-r from-green-50 to-transparent p-6 rounded-xl border border-green-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-green-600" />
                    Live Chat (Tawk.to)
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Enable real-time chat support for your customers using Tawk.to (free, GDPR compliant).
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="tawkto-enabled"
                        checked={settingsForm.tawkto_enabled !== false}
                        onChange={(e) => setSettingsForm({ ...settingsForm, tawkto_enabled: e.target.checked })}
                        className="w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        data-testid="tawkto-enabled-checkbox"
                      />
                      <label htmlFor="tawkto-enabled" className="text-sm font-medium text-gray-700">
                        Enable live chat widget
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Property ID</label>
                        <Input 
                          placeholder="e.g., 1234567890abcdef"
                          value={settingsForm.tawkto_property_id || ''} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, tawkto_property_id: e.target.value })}
                          disabled={settingsForm.tawkto_enabled === false}
                          className={settingsForm.tawkto_enabled === false ? 'opacity-50' : ''}
                          data-testid="tawkto-property-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Found in Tawk.to Dashboard → Settings → Widget</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Widget ID</label>
                        <Input 
                          placeholder="e.g., 1a2b3c4d5e6f"
                          value={settingsForm.tawkto_widget_id || ''} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, tawkto_widget_id: e.target.value })}
                          disabled={settingsForm.tawkto_enabled === false}
                          className={settingsForm.tawkto_enabled === false ? 'opacity-50' : ''}
                          data-testid="tawkto-widget-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Also found in the embed code from Tawk.to</p>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200 mt-4">
                      <p className="text-sm text-gray-600">
                        <strong>How to get your Tawk.to IDs:</strong><br />
                        1. Create a free account at <a href="https://www.tawk.to" target="_blank" rel="noopener noreferrer" className="text-[#003456] underline">tawk.to</a><br />
                        2. Go to Administration → Channels → Chat Widget<br />
                        3. Copy the Property ID and Widget ID from the embed code
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4">Social Media Links</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input placeholder="Facebook URL" value={settingsForm.social_facebook || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_facebook: e.target.value })} />
                    <Input placeholder="Instagram URL" value={settingsForm.social_instagram || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_instagram: e.target.value })} />
                    <Input placeholder="YouTube URL" value={settingsForm.social_youtube || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_youtube: e.target.value })} />
                    <Input placeholder="Twitter/X URL" value={settingsForm.social_twitter || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_twitter: e.target.value })} />
                  </div>
                </div>

                {/* Legal Content */}
                <div className="bg-gradient-to-r from-amber-50 to-transparent p-6 rounded-xl border border-amber-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-amber-600" />
                    Legal Pages Content
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Edit the content of your legal pages. Leave blank to use default content.
                  </p>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Terms of Use</label>
                      <textarea 
                        placeholder="Enter custom Terms of Use content (HTML supported)..." 
                        value={settingsForm.terms_of_use_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, terms_of_use_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                        data-testid="terms-content-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Warranty Policy</label>
                      <textarea 
                        placeholder="Enter custom Warranty content (HTML supported)..." 
                        value={settingsForm.warranty_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, warranty_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                        data-testid="warranty-content-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Policy</label>
                      <textarea 
                        placeholder="Enter custom Privacy Policy content (HTML supported)..." 
                        value={settingsForm.privacy_policy_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, privacy_policy_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                        data-testid="privacy-content-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cancellation Policy</label>
                      <textarea 
                        placeholder="Enter custom Cancellation Policy content (HTML supported)..." 
                        value={settingsForm.cancellation_policy_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, cancellation_policy_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Return Policy</label>
                      <textarea 
                        placeholder="Enter custom Return Policy content (HTML supported)..." 
                        value={settingsForm.return_policy_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, return_policy_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Accessibility</label>
                      <textarea 
                        placeholder="Enter custom Accessibility content (HTML supported)..." 
                        value={settingsForm.accessibility_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, accessibility_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Customer Support</label>
                      <textarea 
                        placeholder="Enter custom Customer Support content (HTML supported)..." 
                        value={settingsForm.customer_support_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, customer_support_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Affiliate Program</label>
                      <textarea 
                        placeholder="Enter custom Affiliate Program content (HTML supported)..." 
                        value={settingsForm.affiliate_program_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, affiliate_program_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Refer a Friend</label>
                      <textarea 
                        placeholder="Enter custom Refer a Friend content (HTML supported)..." 
                        value={settingsForm.refer_a_friend_content || ''} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, refer_a_friend_content: e.target.value })}
                        className="w-full p-3 border rounded-lg resize-none font-mono text-sm"
                        rows={6}
                      />
                    </div>
                  </div>
                </div>

                {/* Referral Program Settings */}
                <div className="bg-gradient-to-r from-green-50 to-transparent p-6 rounded-xl border border-green-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    Referral Program Settings
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Referral Discount (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={settingsForm.referral_discount_percent || 5} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, referral_discount_percent: parseFloat(e.target.value) })}
                        placeholder="5"
                      />
                      <p className="text-xs text-gray-500 mt-1">Discount for referred customers</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Referrer Commission (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={settingsForm.referral_commission_percent || 3} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, referral_commission_percent: parseFloat(e.target.value) })}
                        placeholder="3"
                      />
                      <p className="text-xs text-gray-500 mt-1">Commission earned by referrers</p>
                    </div>
                  </div>
                </div>

                {/* Credit Card Surcharge Settings */}
                <div className="bg-gradient-to-r from-amber-50 to-transparent p-6 rounded-xl border border-amber-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-amber-600" />
                    Credit Card Processing Fee
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="surcharge_enabled"
                        checked={settingsForm.credit_card_surcharge_enabled ?? true}
                        onChange={(e) => setSettingsForm({ ...settingsForm, credit_card_surcharge_enabled: e.target.checked })}
                        className="h-4 w-4 text-amber-600 rounded border-gray-300"
                        data-testid="surcharge-enabled-checkbox"
                      />
                      <label htmlFor="surcharge_enabled" className="text-sm font-medium text-gray-700">
                        Enable credit card processing fee
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Surcharge Percentage (%)</label>
                      <Input 
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={settingsForm.credit_card_surcharge_percent ?? 2.9} 
                        onChange={(e) => setSettingsForm({ ...settingsForm, credit_card_surcharge_percent: parseFloat(e.target.value) || 0 })}
                        placeholder="2.9"
                        disabled={!(settingsForm.credit_card_surcharge_enabled ?? true)}
                        className={!(settingsForm.credit_card_surcharge_enabled ?? true) ? 'opacity-50' : ''}
                        data-testid="surcharge-percent-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Fee added to credit card payments (typically 2.9%). Applies to online checkout and in-store POS. Does not apply to debit, ACH, or e-transfers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Abandoned Cart Email Settings */}
                <div className="bg-gradient-to-r from-orange-50 to-transparent p-6 rounded-xl border border-orange-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-orange-600" />
                    Abandoned Cart Recovery Email
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">Configure the discount offer sent to customers who abandon their cart</p>
                  <div className="space-y-4">
                    {/* KILL SWITCH — disable all abandoned-cart emails site-wide */}
                    <div className={`flex items-start gap-3 p-3 rounded-lg border ${
                      (settingsForm.abandoned_cart_emails_enabled ?? true)
                        ? 'bg-white border-orange-200'
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <input
                        type="checkbox"
                        id="abandoned_cart_emails_enabled"
                        checked={settingsForm.abandoned_cart_emails_enabled ?? true}
                        onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_emails_enabled: e.target.checked })}
                        className="h-5 w-5 mt-0.5 text-orange-600 rounded border-gray-300"
                        data-testid="abandoned-cart-emails-enabled"
                      />
                      <div className="flex-1">
                        <label htmlFor="abandoned_cart_emails_enabled" className="text-sm font-bold text-gray-900 block">
                          Send abandoned cart emails
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          Master switch. When OFF, no abandoned-cart reminder emails are sent — regardless of any other settings below.
                        </p>
                        {!(settingsForm.abandoned_cart_emails_enabled ?? true) && (
                          <p className="text-xs text-red-700 font-semibold mt-2" data-testid="abandoned-cart-emails-paused">
                            Abandoned cart emails are currently PAUSED.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="abandoned_cart_discount_enabled"
                        checked={settingsForm.abandoned_cart_discount_enabled ?? true}
                        onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_discount_enabled: e.target.checked })}
                        className="h-4 w-4 text-orange-600 rounded border-gray-300"
                        data-testid="abandoned-cart-discount-enabled"
                        disabled={!(settingsForm.abandoned_cart_emails_enabled ?? true)}
                      />
                      <label htmlFor="abandoned_cart_discount_enabled" className="text-sm font-medium text-gray-700">
                        Include discount offer in abandoned cart emails
                      </label>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                        <select
                          className="w-full border rounded-lg px-3 py-2"
                          value={settingsForm.abandoned_cart_discount_type || 'percentage'}
                          onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_discount_type: e.target.value })}
                          disabled={!(settingsForm.abandoned_cart_discount_enabled ?? true)}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount ($)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value</label>
                        <Input 
                          type="number"
                          min="0"
                          value={settingsForm.abandoned_cart_discount_value ?? 10} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_discount_value: parseInt(e.target.value) || 0 })}
                          placeholder="10"
                          disabled={!(settingsForm.abandoned_cart_discount_enabled ?? true)}
                          data-testid="abandoned-cart-discount-value"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Code</label>
                        <Input 
                          value={settingsForm.abandoned_cart_discount_code || ''} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_discount_code: e.target.value })}
                          placeholder="COMEBACK10"
                          disabled={!(settingsForm.abandoned_cart_discount_enabled ?? true)}
                          data-testid="abandoned-cart-discount-code"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This code is displayed in the email. Make sure it's created in your Coupons section.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Delay (hours)</label>
                        <Input 
                          type="number"
                          min="1"
                          max="24"
                          value={settingsForm.abandoned_cart_email_delay_hours ?? 1} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, abandoned_cart_email_delay_hours: parseInt(e.target.value) || 1 })}
                          placeholder="1"
                          disabled={!(settingsForm.abandoned_cart_discount_enabled ?? true)}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Hours to wait before sending the first recovery email
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* External Configurator URL Settings */}
                <div className="bg-gradient-to-r from-purple-50 to-transparent p-6 rounded-xl border border-purple-100">
                  <h2 className="text-xl font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-purple-600" />
                    Build My Freeze Dryer Link
                  </h2>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">External Configurator URL</label>
                    <Input 
                      value={settingsForm.configurator_url || ''} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, configurator_url: e.target.value })}
                      placeholder="https://configurator.example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      External URL for "Build My Freeze Dryer" feature. Opens in a new tab. Leave blank to use internal configurator.
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <Button type="submit" className="bg-[#003456] hover:bg-[#001d30]" data-testid="save-settings-btn">
                    Save Settings
                  </Button>
                </div>
              </form>
            </div>

            {/* SEO Info */}
            <div className="mt-8 bg-[#dbeef5] rounded-2xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-3">SEO Features Enabled</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2"><span className="text-[#003456]">✓</span> Schema.org structured data (Product, Recipe, Article, FAQ)</li>
                <li className="flex items-center gap-2"><span className="text-[#003456]">✓</span> Open Graph & Twitter meta tags</li>
                <li className="flex items-center gap-2"><span className="text-[#003456]">✓</span> Automatic sitemap generation at /api/sitemap.xml</li>
                <li className="flex items-center gap-2"><span className="text-[#003456]">✓</span> robots.txt for search engine crawlers</li>
                <li className="flex items-center gap-2"><span className="text-[#003456]">✓</span> AI-optimized content structure for search engines</li>
              </ul>
            </div>
          </div>
        )}

        {/* Pages */}
        {activeSection === 'pages' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#0a0203]" style={{  }}>Pages</h1>
              <Link to="/admin/pages/builder">
                <Button className="bg-[#003456] hover:bg-[#001d30]" data-testid="create-page-btn">
                  <Plus className="h-5 w-5 mr-2" /> Create Page
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pages.map(page => (
                <div key={page.id} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-[#0a0203]" style={{  }}>{page.title}</h3>
                      <p className="text-sm text-gray-500">/{page.slug}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${page.published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                      {page.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/pages/builder/${page.id}`}>
                      <Button size="sm" variant="outline" data-testid={`edit-page-${page.id}`}>
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    </Link>
                    {page.published && (
                      <Link to={`/page/${page.slug}`} target="_blank">
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                      </Link>
                    )}
                    <button onClick={() => handleDeletePage(page.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              <Link to="/admin/pages/builder" className="block">
                <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-dashed border-gray-200 hover:border-[#003456] transition-colors cursor-pointer h-full flex items-center justify-center min-h-[180px]">
                  <div className="text-center">
                    <Plus className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Create New Page</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Financing */}
        {/* Financing - Part of Pages/Website Control */}
        {activeSection === 'pages' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#0a0203]" style={{  }}>Financing Embeds</h1>
              <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#003456] hover:bg-[#001d30]" data-testid="add-embed-btn">
                    <Plus className="h-5 w-5 mr-2" /> Add Embed
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Financing Embed</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateEmbed} className="space-y-4">
                    <Input placeholder="Partner Name" value={embedForm.name} onChange={(e) => setEmbedForm({ ...embedForm, name: e.target.value })} required data-testid="embed-name-input" />
                    <Input placeholder="Embed URL" value={embedForm.embed_url} onChange={(e) => setEmbedForm({ ...embedForm, embed_url: e.target.value })} required data-testid="embed-url-input" />
                    <Input placeholder="Description (optional)" value={embedForm.description} onChange={(e) => setEmbedForm({ ...embedForm, description: e.target.value })} />
                    <Button type="submit" className="w-full bg-[#003456] hover:bg-[#001d30]" data-testid="create-embed-submit">Add Embed</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {embeds.map(embed => (
                <div key={embed.id} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center">
                        <LinkIcon className="h-6 w-6 text-[#003456]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0a0203]">{embed.name}</h3>
                        <p className="text-sm text-gray-500 truncate max-w-md">{embed.embed_url}</p>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteEmbed(embed.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-embed-${embed.id}`}>
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
              {embeds.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                  <LinkIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No financing embeds added yet</p>
                  <p className="text-sm text-gray-400 mt-2">Add financing partner links to embed on your financing page</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bug Reports Management */}
        {activeSection === 'bug-reports' && (
          <div className="animate-fade-in">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-[#0a0203]">Bug Reports</h1>
              <p className="text-gray-600 mt-1">Manage user-reported bugs and issues</p>
            </div>
            <BugReportsManager />
          </div>
        )}

        {/* Email Templates Editor */}
        {activeSection === 'email-templates' && (
          <div className="animate-fade-in">
            <EmailTemplatesEditor />
          </div>
        )}

        {/* Payout Scheduler */}
        {activeSection === 'payout-scheduler' && (
          <div className="animate-fade-in">
            <PayoutSchedulerSettings />
          </div>
        )}

        {/* Security & Team Management */}
        {activeSection === 'security' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-[#0a0203]" style={{  }}>Security & Team</h1>
                <p className="text-gray-600 mt-1">Manage team members and access permissions</p>
              </div>
              <Dialog open={showAddTeamMemberDialog} onOpenChange={setShowAddTeamMemberDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#003456] hover:bg-[#001d30]" data-testid="add-team-member-btn">
                    <UserPlus className="h-5 w-5 mr-2" /> Add Team Member
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-[#003456]" />
                      Add Team Member
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.post(
                        `${API_URL}/api/team/invite?name=${encodeURIComponent(teamMemberForm.name)}&email=${encodeURIComponent(teamMemberForm.email)}&role=${teamMemberForm.role}`,
                        { permissions: teamMemberForm.permissions },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      if (res.data.email_sent) {
                        toast.success(`Invitation sent to ${teamMemberForm.email}`);
                      } else {
                        toast.warning(`Invitation created but email may not have been sent. Share this link: ${res.data.invite_url || 'Check admin panel'}`);
                      }
                      
                      // Refresh team members list
                      const membersRes = await axios.get(`${API_URL}/api/team/members`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setTeamMembers(membersRes.data.members || []);
                      
                      setShowAddTeamMemberDialog(false);
                      setTeamMemberForm({ name: '', email: '', role: 'editor', permissions: [] });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to send invitation');
                    }
                  }} className="space-y-4 pt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <Input
                        required
                        value={teamMemberForm.name}
                        onChange={(e) => setTeamMemberForm({ ...teamMemberForm, name: e.target.value })}
                        placeholder="John Doe"
                        data-testid="team-member-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <Input
                        type="email"
                        required
                        value={teamMemberForm.email}
                        onChange={(e) => setTeamMemberForm({ ...teamMemberForm, email: e.target.value })}
                        placeholder="john@example.com"
                        data-testid="team-member-email"
                      />
                      <p className="text-xs text-gray-500 mt-1">An invitation will be sent to this email</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={teamMemberForm.role}
                        onChange={(e) => setTeamMemberForm({ ...teamMemberForm, role: e.target.value })}
                        className="w-full p-3 border rounded-lg bg-white"
                        data-testid="team-member-role"
                      >
                        <option value="admin">Admin - Full Access</option>
                        <option value="manager">Manager - Can manage products, orders, and content</option>
                        <option value="editor">Editor - Can manage content only</option>
                        <option value="viewer">Viewer - Read-only access</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['Products', 'Orders', 'Content', 'Marketing', 'Settings', 'Reports'].map(perm => (
                          <label key={perm} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                              type="checkbox"
                              checked={teamMemberForm.permissions.includes(perm.toLowerCase())}
                              onChange={(e) => {
                                const p = perm.toLowerCase();
                                setTeamMemberForm(prev => ({
                                  ...prev,
                                  permissions: e.target.checked 
                                    ? [...prev.permissions, p]
                                    : prev.permissions.filter(x => x !== p)
                                }));
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">{perm}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowAddTeamMemberDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#003456]">
                        <Mail className="h-4 w-4 mr-2" /> Send Invitation
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Current User Info */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#003456]" />
                Your Account
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#003456] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-[#0a0203]">{user?.email}</p>
                    <p className="text-sm text-gray-500">Owner / Super Admin</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Active</span>
              </div>
            </div>

            {/* Team Members List */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 border-b">
                <h3 className="font-semibold text-[#0a0203] flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#003456]" />
                  Team Members ({teamMembers.length})
                </h3>
              </div>
              
              {teamMembers.length > 0 ? (
                <div className="divide-y">
                  {teamMembers.map(member => (
                    <div key={member.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium">
                          {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[#0a0203]">{member.name || 'Pending'}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                          member.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                          member.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                          member.role === 'editor' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {member.role}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          member.status === 'active' ? 'bg-green-100 text-green-700' :
                          member.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {member.status}
                        </span>
                        <button 
                          onClick={() => {
                            setTeamMembers(teamMembers.filter(m => m.id !== member.id));
                            toast.success('Team member removed');
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          data-testid={`remove-team-member-${member.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No team members yet</p>
                  <p className="text-sm text-gray-400 mt-2">Add team members to help manage your website</p>
                </div>
              )}
            </div>

            {/* Role Descriptions */}
            <div className="mt-6 bg-[#dbeef5] rounded-2xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5 text-[#003456]" />
                Role Permissions
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4">
                  <h4 className="font-medium text-purple-700 mb-2">Admin</h4>
                  <p className="text-sm text-gray-600">Full access to all features including settings and team management</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <h4 className="font-medium text-blue-700 mb-2">Manager</h4>
                  <p className="text-sm text-gray-600">Can manage products, orders, marketing, and content</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <h4 className="font-medium text-green-700 mb-2">Editor</h4>
                  <p className="text-sm text-gray-600">Can create and edit content, blogs, and recipes</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <h4 className="font-medium text-gray-700 mb-2">Viewer</h4>
                  <p className="text-sm text-gray-600">Read-only access to view data and reports</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
